/* global document, window */
/* jshint scripturl: true */

/**
 * This script is used by the parent page to embed the widget.
 * It creates an iframe and injects the HTML, CSS and JS
 * needed to run the widget.
 */

'use strict';

import EventEmitter from 'common/event-emitter';

function escapeHtml(unsafe) {
  const text = document.createTextNode(unsafe);
  const div = document.createElement('div');
  div.appendChild(text);
  return div.innerHTML;
}

export default class Widget extends EventEmitter {
  constructor(widgetData) {
    super();

    this.processMessageFromWidget = this.processMessageFromWidget.bind(this);

    // Generate the configuration that we need to
    // create the widget.
    this.config = {
      zIndex: 9997,
    };

    let channel;

    // If channel data was passed in, and the correct
    // data is present, use it as widget config.
    const data = widgetData['web'];
    const label = data.label || 'Web';
    Object.assign(data, {
      key: 'web',
      label: label,
      labelWidth: label.length * 10, // Auto calc label width
    });
    channel = data;
    this.config.channel = channel;

    // Grab some additional data that may be set.
    [
      'theme',
      'revealAfterSecs',
      'welcomeRevealAfterSecs',
      'welcomeHideAfterSecs',
      'hideWelcomeForDays',
      'userId',
      'userPaidPlan',
      'zIndex',
      'popupHeight',
      'themeCssUrl',
      'customCssUrl',
      'themeName',
      'themeCssOnly',
      'widgetIconUrl',
      'widgetPosition',
    ].forEach((k) => {
      if (widgetData.hasOwnProperty(k)) {
        this.config[k] = widgetData[k];
      }
    });

    // Grab any additional data that must be escaped.
    ['welcomeText'].forEach((k) => {
      if (widgetData.hasOwnProperty(k)) {
        this.config[k] = escapeHtml(widgetData[k]);
      }
    });
  }

  render() {
    if (!this.iframe) {
      const position = this.config.widgetPosition || 'right';
      const iframeStyles = `
        .fxo-widget-iframe {
          border: none;
          display: block;
          position: fixed;
          bottom: 15px;
          ${position}: 0;
          z-index: ${this.config.zIndex};
        }
        @media (min-width: 769px) {
          .fxo-widget-iframe {
            ${position}: 10px;
          }
        }
        @media (min-width: 1025px) {
          .fxo-widget-iframe {
            bottom: 25px;
            ${position}: 20px;
          }
        }
      `;

      if (this.config.themeCssUrl) {
        const themePref = document.createElement('link');
        themePref.href = this.config.themeCssUrl;
        themePref.rel = 'preload';
        themePref.as = 'style';
        document.head.appendChild(themePref);
      } else {
        const themePref = document.createElement('link');
        const themeBaseUrl =
          this.config.themeBaseUrl || 'https://d2u2e8j2jrnyta.cloudfront.net';
        const themeName = this.config.themeName || 'classic';
        themePref.href = `${themeBaseUrl}/${themeName}.css`;
        themePref.rel = 'preload';
        themePref.as = 'style';
        document.head.appendChild(themePref);
      }
      if (this.config.customCssUrl) {
        const themePref = document.createElement('link');
        themePref.href = this.config.customCssUrl;
        themePref.rel = 'preload';
        themePref.as = 'style';
        document.head.appendChild(themePref);
      }

      const style = document.createElement('style');
      style.type = 'text/css';
      style.appendChild(document.createTextNode(iframeStyles));
      document.head.appendChild(style);

      // Create iframe and add it to the host.
      this.iframe = document.createElement('iframe');
      this.iframe.id = 'fxo-widget-iframe';
      this.iframe.className = 'fxo-widget-iframe';
      this.iframe.scrolling = 'no';
      this.iframe.width = 100;
      this.iframe.height = 100;
      this.iframe.title = 'Chatbot';
      this.iframe.src = process.env.WIDGET_URL;
      this.iframe.allowFullscreen = true;

      document.body.appendChild(this.iframe);

      // Listen for messages from the iframe
      window.addEventListener('message', this.processMessageFromWidget, false);
    }
  }

  destroy() {
    window.removeEventListener('message', this.processMessageFromWidget, false);
    this.sendMessageToWidget('widget:destroy');
    document.body.removeChild(this.iframe);
  }

  // Process any received messages
  processMessageFromWidget(payload) {
    let message;
    try {
      message = JSON.parse(payload.data);
    } catch (e) {
      // Not for us
      return;
    }

    switch (message.action) {
      case 'fxo-widget-ready':
        // Send the widget our config.
        this.sendMessageToWidget('widget:init', this.config);
        break;
      case 'fxo-widget-resize':
        // Resize the iframe.
        if (this.iframe) {
          this.iframe.width = message.payload.width;
          this.iframe.height = message.payload.height;
        }
        break;
      case 'fxo-widget-reveal-web-messenger':
        // Show the web messenger.
        this.emit('revealWebMessenger');
        break;
    }
  }

  sendMessageToWidget(action, payload = {}) {
    // Send message to iframe.
    if (this.iframe && this.iframe.contentWindow) {
      const message = JSON.stringify({ action, payload });
      this.iframe.contentWindow.postMessage(message, '*');
    }
  }

  getPublicAPI() {
    return {
      destroy: this.destroy.bind(this),
    };
  }
}



// WEBPACK FOOTER //
// ./src/widget/embed.js